import { Link } from 'react-router-dom'

export default function HowItWorks() {
  return (
    <div className="py-16 px-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-semibold text-text-primary mb-4">
          How This Actually Works
        </h1>
        <p className="text-lg text-text-secondary mb-12">
          Not marketing speak. The actual architecture makes it impossible 
          for us to access your data.
        </p>

        {/* Architecture */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            The architecture
          </h2>
          <div className="card">
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-text-primary mb-2">
                  1. No backend. None.
                </h3>
                <p className="text-sm text-text-secondary">
                  This is a static web app. It runs in your browser. There's no server 
                  waiting to receive your data. We host HTML, CSS, and JavaScript. That's it.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-2">
                  2. Your Google Drive is your database
                </h3>
                <p className="text-sm text-text-secondary">
                  When you connect your Google account, we create a folder called{' '}
                  <code className="bg-[#F5F5F3] px-1.5 py-0.5 rounded text-xs font-mono">
                    PraxisCRM_Data
                  </code>{' '}
                  in YOUR Drive. Your companies, contacts, deals, activities. All stored as 
                  JSON files in YOUR folder on YOUR account.
                </p>
              </div>

              <div>
                <h3 className="font-medium text-text-primary mb-2">
                  3. Everything happens in your browser
                </h3>
                <p className="text-sm text-text-secondary">
                  Create a deal. Update a contact. Filter your pipeline. All of it happens 
                  locally. Data moves from your browser to your Drive. We're not in the middle and we never will be.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Data flow */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            The data flow
          </h2>
          <div className="space-y-4">
            <div className="card bg-[#F5F5F3] border-l-4 border-l-action-primary">
              <p className="font-mono text-sm text-text-primary">
                You --&gt; Your Browser --&gt; Google Drive API --&gt; Your Drive
              </p>
            </div>
            <p className="text-sm text-text-secondary">
              Notice what's missing: us. Your data flows from your browser straight to Google. 
              We don't intercept it. We don't log it. We don't even see it pass by.
            </p>
          </div>
        </section>

        {/* OAuth scope */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Nerd shit
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              We use Google's{' '}
              <code className="bg-[#F5F5F3] px-1.5 py-0.5 rounded text-xs font-mono">
                drive.file
              </code>{' '}
              scope. The most restrictive file access Google offers:
            </p>
            <ul className="space-y-2 text-sm text-text-secondary">
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>We can only touch files Praxis creates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Your existing Drive files? Invisible to us</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Files from other apps? Can't see those either</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-success mt-0.5">+</span>
                <span>Revoke access whenever you want. One click in Google settings.</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Sync logic */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            Sync and data integrity
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              We use a sequential write queue to keep your data safe:
            </p>
            <ol className="space-y-2 text-sm text-text-secondary list-decimal list-inside">
              <li>Changes appear instantly in your browser (optimistic updates)</li>
              <li>Writes queue up and process one at a time</li>
              <li>Each write finishes before the next starts</li>
              <li>The UI tells you when data is safely stored</li>
            </ol>
          </div>
        </section>

        {/* Proxy layer */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            The proxy layer (the one exception)
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              For prospect research and AI features, we run a thin proxy layer. Here's why and what it does:
            </p>
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-text-primary mb-2">
                  Why a proxy?
                </h3>
                <p className="text-sm text-text-secondary">
                  Browsers block direct requests to other websites (CORS). To fetch company websites 
                  and call AI providers, requests need to go through a server. Our proxy is that server.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-text-primary mb-2">
                  What passes through?
                </h3>
                <ul className="space-y-1 text-sm text-text-secondary list-disc list-inside">
                  <li>Website HTML for companies you research</li>
                  <li>Companies House API requests (UK company registry)</li>
                  <li>AI provider requests (using your API key)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-text-primary mb-2">
                  What we don't do
                </h3>
                <ul className="space-y-1 text-sm text-text-secondary list-disc list-inside">
                  <li>No logging of request content</li>
                  <li>No storage of responses</li>
                  <li>No tracking of what you research</li>
                  <li>No access to your AI API keys (encrypted in your Drive)</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* AI integration */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold text-text-primary mb-4">
            AI: yours, not ours
          </h2>
          <div className="card">
            <p className="text-sm text-text-secondary mb-4">
              Praxis doesn't have an AI. You bring your own:
            </p>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">Your key</span>
                  <span className="text-sm text-text-secondary">
                    You provide your own API key from OpenAI, Anthropic, Gemini, or OpenRouter. 
                    Your costs, your rate limits, your control.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">Encrypted</span>
                  <span className="text-sm text-text-secondary">
                    Your API key is encrypted in your browser using keys derived from your Google account. 
                    The encrypted key is stored in your Drive. We never see the plaintext.
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="bg-[#F5F5F3] rounded px-2 py-1 text-xs font-mono shrink-0">Proxied</span>
                  <span className="text-sm text-text-secondary">
                    AI requests pass through our proxy (for CORS), but your decrypted key only exists 
                    in your browser. We forward the request; we don't read it.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-8 border-t border-border">
          <p className="text-text-secondary mb-4">
            Want to understand the privacy implications?{' '}
            <Link to="/privacy" className="text-action-primary hover:underline">
              Read the privacy breakdown
            </Link>
          </p>
          <Link to="/login" className="btn btn-primary">
            Connect Google Drive
          </Link>
        </section>
      </div>
    </div>
  )
}
